import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Table, Tabs, Button, message, Modal, Form, Input, Select, Space, Badge, Descriptions, Tag } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/http';

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

const getRecordType = (record) => record?.application?.record_type;

const getStartAt = (record) => {
  const startDate = record?.application?.start_date;
  const startHour = record?.application?.start_time;
  if (!startDate) return null;
  const hour = startHour === undefined || startHour === null || startHour === '' ? 0 : Number(startHour);
  const safeHour = Number.isFinite(hour) ? hour : 0;
  const dt = dayjs(startDate).hour(safeHour).minute(0).second(0);
  return dt.isValid() ? dt : null;
};

const getSortKeyPending = (record) => {
  const startAt = getStartAt(record);
  if (startAt) return startAt.valueOf();
  const applyTime = record?.application?.apply_time;
  const applyAt = applyTime ? dayjs(applyTime) : null;
  return applyAt && applyAt.isValid() ? applyAt.valueOf() : 0;
};

const getSortKeyProcessed = (record) => {
  const approvedAt = record?.approved_at ? dayjs(record.approved_at) : null;
  if (approvedAt && approvedAt.isValid()) return approvedAt.valueOf();
  const applyTime = record?.application?.apply_time;
  const applyAt = applyTime ? dayjs(applyTime) : null;
  if (applyAt && applyAt.isValid()) return applyAt.valueOf();
  const startAt = getStartAt(record);
  return startAt && startAt.isValid() ? startAt.valueOf() : 0;
};

const ApprovalPage = () => {
  const navigate = useNavigate();
  const { applicationId, type } = useParams();
  const [activeTab, setActiveTab] = useState('pending');
  const [pendingApplications, setPendingApplications] = useState([]);
  const [processedApplications, setProcessedApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [currentApplication, setCurrentApplication] = useState(null);
  const [approvalForm] = Form.useForm();
  const [approving, setApproving] = useState(false);

  const [isMobile, setIsMobile] = useState(false);
  const [quickType, setQuickType] = useState('ALL');
  const [quickRange, setQuickRange] = useState('ALL');
  const [pendingVisibleCount, setPendingVisibleCount] = useState(10);
  const [processedVisibleCount, setProcessedVisibleCount] = useState(10);

  // 状态标签映射
  const statusConfig = {
    PENDING: { text: '待审批', color: 'processing' },
    APPROVED: { text: '已批准', color: 'success' },
    REJECTED: { text: '已拒绝', color: 'error' }
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(!!mediaQuery.matches);
    update();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', update);
      return () => mediaQuery.removeEventListener('change', update);
    }
    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, []);

  // 获取待审批列表
  const fetchPendingApplications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/leave-overtime/approval/pending');
      if (response && response.data) {
        setPendingApplications(response.data);
      }
    } catch (error) {
      console.error('获取待审批列表失败:', error);
      message.error('获取待审批列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取已审批列表
  const fetchProcessedApplications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/leave-overtime/approval/processed');
      if (response && response.data) {
        setProcessedApplications(response.data);
      }
    } catch (error) {
      console.error('获取已审批列表失败:', error);
      message.error('获取已审批列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始化数据
  useEffect(() => {
    if (applicationId && type) {
      // 如果有具体申请ID，直接查看详情
      fetchApplicationDetails(applicationId, type);
    } else {
      // 否则显示列表
      fetchPendingApplications();
    }
  }, [applicationId, type]);

  // 标签页切换时重新加载数据
  const handleTabChange = (key) => {
    setActiveTab(key);
    setQuickType('ALL');
    setQuickRange('ALL');
    setPendingVisibleCount(10);
    setProcessedVisibleCount(10);
    if (key === 'processed') {
      fetchProcessedApplications();
    } else {
      fetchPendingApplications();
    }
  };

  const pendingForDisplay = useMemo(() => {
    const list = Array.isArray(pendingApplications) ? pendingApplications : [];
    const now = dayjs();
    const matchesType = (record) => {
      if (quickType === 'ALL') return true;
      if (quickType === 'LEAVE') return getRecordType(record) === 'LEAVE';
      if (quickType === 'OVERTIME') return getRecordType(record) === 'OVERTIME';
      return true;
    };
    const matchesRange = (record) => {
      if (quickRange === 'ALL') return true;
      const base = getStartAt(record);
      if (!base || !base.isValid()) return false;
      if (quickRange === 'TODAY') return base.isSame(now, 'day');
      if (quickRange === '7D') return base.isAfter(now.startOf('day').subtract(6, 'day'));
      if (quickRange === '30D') return base.isAfter(now.startOf('day').subtract(29, 'day'));
      return true;
    };
    const filtered = list.filter(r => matchesType(r) && matchesRange(r));
    // 待审批：越临近（开始时间越靠近“现在/近期”）排越前 => 按开始时间升序；缺失则按申请时间降序兜底
    return filtered.sort((a, b) => {
      const aKey = getSortKeyPending(a);
      const bKey = getSortKeyPending(b);
      if (aKey === bKey) return 0;
      return aKey - bKey;
    });
  }, [pendingApplications, quickType, quickRange]);

  const processedForDisplay = useMemo(() => {
    const list = Array.isArray(processedApplications) ? processedApplications : [];
    const now = dayjs();
    const matchesType = (record) => {
      if (quickType === 'ALL') return true;
      if (quickType === 'LEAVE') return getRecordType(record) === 'LEAVE';
      if (quickType === 'OVERTIME') return getRecordType(record) === 'OVERTIME';
      return true;
    };
    const matchesRange = (record) => {
      if (quickRange === 'ALL') return true;
      const approvedAt = record?.approved_at ? dayjs(record.approved_at) : null;
      const base = approvedAt && approvedAt.isValid() ? approvedAt : getStartAt(record);
      if (!base || !base.isValid()) return false;
      if (quickRange === 'TODAY') return base.isSame(now, 'day');
      if (quickRange === '7D') return base.isAfter(now.startOf('day').subtract(6, 'day'));
      if (quickRange === '30D') return base.isAfter(now.startOf('day').subtract(29, 'day'));
      return true;
    };
    const filtered = list.filter(r => matchesType(r) && matchesRange(r));
    // 已审批：越近的排前面 => 按审批时间倒序（最近优先）
    return filtered.sort((a, b) => {
      const aKey = getSortKeyProcessed(a);
      const bKey = getSortKeyProcessed(b);
      if (aKey === bKey) return 0;
      return bKey - aKey;
    });
  }, [processedApplications, quickType, quickRange]);

  const renderQuickFilters = () => {
    const typeButtons = [
      { key: 'ALL', text: '全部' },
      { key: 'LEAVE', text: '请假' },
      { key: 'OVERTIME', text: '加班' },
    ];
    const rangeButtons = [
      { key: 'ALL', text: '不限时间' },
      { key: 'TODAY', text: '今天' },
      { key: '7D', text: '近7天' },
      { key: '30D', text: '近30天' },
    ];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {typeButtons.map(b => (
            <Button
              key={b.key}
              size="small"
              type={quickType === b.key ? 'primary' : 'default'}
              onClick={() => { setQuickType(b.key); setPendingVisibleCount(10); setProcessedVisibleCount(10); }}
            >
              {b.text}
            </Button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {rangeButtons.map(b => (
            <Button
              key={b.key}
              size="small"
              type={quickRange === b.key ? 'primary' : 'default'}
              onClick={() => { setQuickRange(b.key); setPendingVisibleCount(10); setProcessedVisibleCount(10); }}
            >
              {b.text}
            </Button>
          ))}
        </div>
      </div>
    );
  };

  const renderMobileCard = (record, mode) => {
    const application = record?.application || {};
    const isLeave = application.record_type === 'LEAVE';

    const startDate = application.start_date ? dayjs(application.start_date).format('YYYY-MM-DD') : '-';
    const endDate = application.end_date ? dayjs(application.end_date).format('YYYY-MM-DD') : '-';
    const startTime = application.start_time !== undefined && application.start_time !== null && application.start_time !== '' ? `${application.start_time}点` : '';
    const endTime = application.end_time !== undefined && application.end_time !== null && application.end_time !== '' ? `${application.end_time}点` : '';

    const headerLeft = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Tag color={isLeave ? 'blue' : 'purple'}>{isLeave ? '请假' : '加班'}</Tag>
        <span style={{ fontWeight: 600 }}>{application.employee_name || '—'}</span>
        {application.department ? <span style={{ color: '#64748b', fontSize: 12 }}>{application.department}</span> : null}
      </div>
    );

    const headerRight = mode === 'pending'
      ? <Badge status="processing" text="待审批" />
      : <Badge status={statusConfig[record.status]?.color || 'default'} text={statusConfig[record.status]?.text || record.status} />;

    return (
      <Card
        key={record.approval_id}
        size="small"
        style={{ borderRadius: 12, marginBottom: 10 }}
        bodyStyle={{ padding: 12 }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>{headerLeft}</div>
          <div style={{ whiteSpace: 'nowrap' }}>{headerRight}</div>
        </div>

        <div style={{ marginTop: 8, color: '#111827' }}>
          <div style={{ fontSize: 13 }}>
            {isLeave ? '请假类型' : '加班类型'}：{application.leave_type || application.overtime_type || '—'}
          </div>
          <div style={{ fontSize: 13, marginTop: 4 }}>
            时间：{startDate} {startTime} 至 {endDate} {endTime}
          </div>
          <div style={{ fontSize: 13, marginTop: 4 }}>
            时长：{application.duration ?? '—'} 小时
          </div>
          {mode === 'processed' && record.approved_at && (
            <div style={{ fontSize: 12, marginTop: 6, color: '#64748b' }}>
              审批时间：{dayjs(record.approved_at).format('YYYY-MM-DD HH:mm')}
            </div>
          )}
          {mode === 'pending' && application.apply_time && (
            <div style={{ fontSize: 12, marginTop: 6, color: '#64748b' }}>
              申请时间：{dayjs(application.apply_time).format('YYYY-MM-DD HH:mm')}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
          <Button type="primary" size="small" onClick={() => handleViewDetails(record)}>
            {mode === 'pending' ? '去审批' : '查看'}
          </Button>
        </div>
      </Card>
    );
  };

  const renderMobileList = (mode) => {
    const list = mode === 'pending' ? pendingForDisplay : processedForDisplay;
    const visibleCount = mode === 'pending' ? pendingVisibleCount : processedVisibleCount;
    const setVisibleCount = mode === 'pending' ? setPendingVisibleCount : setProcessedVisibleCount;
    const visible = list.slice(0, visibleCount);

    return (
      <div>
        {renderQuickFilters()}
        {visible.map(r => renderMobileCard(r, mode))}
        {!visible.length && (
          <div style={{ padding: 12, color: '#64748b', textAlign: 'center' }}>暂无数据</div>
        )}
        {list.length > visibleCount && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
            <Button onClick={() => setVisibleCount(c => c + 10)}>加载更多</Button>
          </div>
        )}
      </div>
    );
  };

  // 获取申请详情
  const fetchApplicationDetails = async (id, applicationType) => {
    try {
      setLoading(true);
      const response = await api.get(`/leave-overtime/approval/application/${applicationType}/${id}`);
      if (response && response.data) {
        setCurrentApplication(response.data);
        setModalVisible(true);
      }
    } catch (error) {
      console.error('获取申请详情失败:', error);
      message.error('获取申请详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 查看申请详情
  const handleViewDetails = (record) => {
    setCurrentApplication(record);
    setModalVisible(true);
  };

  // 提交审批
  const handleApproval = async (values) => {
    try {
      setApproving(true);
      
      const approvalData = {
        action: values.action,
        comments: values.comments || ''
      };

      const response = await api.put(
        `/leave-overtime/approval/${currentApplication.approval_id}`,
        approvalData
      );

      if (response?.data?.success) {
        message.success(`${values.action === 'APPROVE' ? '审批通过' : '审批拒绝'}成功`);
        setModalVisible(false);
        approvalForm.resetFields();
        
        // 重新加载待审批列表
        fetchPendingApplications();
      } else {
        message.error(response?.data?.message || response?.message || '审批操作失败');
      }
    } catch (error) {
      console.error('审批操作失败:', error);
      message.error('审批操作失败，请稍后重试');
    } finally {
      setApproving(false);
    }
  };

  // 待审批列表列配置
  const pendingColumns = [
    {
      title: '申请类型',
      dataIndex: ['application', 'record_type'],
      key: 'record_type',
      render: (type) => {
        return type === 'LEAVE' ? '请假申请' : '加班申请';
      }
    },
    {
      title: '申请人',
      dataIndex: ['application', 'employee_name'],
      key: 'employee_name'
    },
    {
      title: '部门',
      dataIndex: ['application', 'department'],
      key: 'department'
    },
    {
      title: '请假/加班类型',
      dataIndex: ['application', 'leave_type'],
      key: 'leave_type',
      render: (type, record) => {
        return record.application.leave_type || record.application.overtime_type;
      }
    },
    {
      title: '开始日期',
      dataIndex: ['application', 'start_date'],
      key: 'start_date',
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-'
    },
    {
      title: '结束日期',
      dataIndex: ['application', 'end_date'],
      key: 'end_date',
      render: (value) => value ? dayjs(value).format('YYYY-MM-DD') : '-'
    },
    {
      title: '申请时长(小时)',
      dataIndex: ['application', 'duration'],
      key: 'duration'
    },
    {
      title: '申请时间',
      dataIndex: ['application', 'apply_time'],
      key: 'apply_time',
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button type="link" onClick={() => handleViewDetails(record)}>
          审批
        </Button>
      )
    }
  ];

  // 已审批列表列配置
  const processedColumns = [
    {
      title: '申请类型',
      dataIndex: ['application', 'record_type'],
      key: 'record_type',
      render: (type) => {
        return type === 'LEAVE' ? '请假申请' : '加班申请';
      }
    },
    {
      title: '申请人',
      dataIndex: ['application', 'employee_name'],
      key: 'employee_name'
    },
    {
      title: '部门',
      dataIndex: ['application', 'department'],
      key: 'department'
    },
    {
      title: '请假/加班类型',
      dataIndex: ['application', 'leave_type'],
      key: 'leave_type',
      render: (type, record) => {
        return record.application.leave_type || record.application.overtime_type;
      }
    },
    {
      title: '审批结果',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge status={statusConfig[status]?.color || 'default'} text={statusConfig[status]?.text || status} />
      )
    },
    {
      title: '审批时间',
      dataIndex: 'approved_at',
      key: 'approved_at',
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button type="link" onClick={() => handleViewDetails(record)}>
          查看
        </Button>
      )
    }
  ];

  // 渲染申请详情
  const renderApplicationDetails = () => {
    if (!currentApplication) return null;
    
    const { application } = currentApplication;
    const isLeave = application.record_type === 'LEAVE';

    return (
      <Card title={`${isLeave ? '请假' : '加班'}申请详情`}>
        <Descriptions column={1} bordered>
          <Descriptions.Item label="申请类型">
            {isLeave ? '请假申请' : '加班申请'}
          </Descriptions.Item>
          <Descriptions.Item label="申请人">
            {application.employee_name} (ID: {application.employee_id})
          </Descriptions.Item>
          <Descriptions.Item label="部门">
            {application.department}
          </Descriptions.Item>
          <Descriptions.Item label={`${isLeave ? '请假' : '加班'}类型`}>
            {application.leave_type || application.overtime_type}
          </Descriptions.Item>
          <Descriptions.Item label="申请日期">
            {application.start_date ? dayjs(application.start_date).format('YYYY-MM-DD') : '-'} 至 {application.end_date ? dayjs(application.end_date).format('YYYY-MM-DD') : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="申请时长">
            {application.duration} 小时
          </Descriptions.Item>
          {isLeave ? (
            <>
              <Descriptions.Item label="每日开始时间">
                {application.start_time} 点
              </Descriptions.Item>
              <Descriptions.Item label="每日结束时间">
                {application.end_time} 点
              </Descriptions.Item>
              <Descriptions.Item label="紧急联系人">
                {application.emergency_contact || '无'}
              </Descriptions.Item>
            </>
          ) : (
            <>
              <Descriptions.Item label="每日开始时间">
                {application.start_time} 点
              </Descriptions.Item>
              <Descriptions.Item label="每日结束时间">
                {application.end_time} 点
              </Descriptions.Item>
              <Descriptions.Item label="项目名称">
                {application.project_name || '无'}
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label="申请原因">
            {application.reason}
          </Descriptions.Item>
          <Descriptions.Item label="申请时间">
            {application.apply_time ? dayjs(application.apply_time).format('YYYY-MM-DD HH:mm:ss') : '-'}
          </Descriptions.Item>
          {currentApplication.status && (
            <Descriptions.Item label="审批状态">
              <Badge 
                status={statusConfig[currentApplication.status]?.color || 'default'} 
                text={statusConfig[currentApplication.status]?.text || currentApplication.status} 
              />
            </Descriptions.Item>
          )}
          {currentApplication.approved_at && (
            <Descriptions.Item label="审批时间">
              {dayjs(currentApplication.approved_at).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
          )}
          {currentApplication.approver_name && (
            <Descriptions.Item label="审批人">
              {currentApplication.approver_name}
            </Descriptions.Item>
          )}
          {currentApplication.comments && (
            <Descriptions.Item label="审批意见">
              {currentApplication.comments}
            </Descriptions.Item>
          )}
        </Descriptions>

        {currentApplication.status === 'PENDING' && (
          <div style={{ marginTop: 20 }}>
            <Form form={approvalForm} layout="vertical" onFinish={handleApproval}>
              <Form.Item
                name="action"
                label="审批结果"
                rules={[{ required: true, message: '请选择审批结果' }]}
              >
                <Select placeholder="请选择审批结果">
                  <Option value="APPROVE">同意</Option>
                  <Option value="REJECT">拒绝</Option>
                </Select>
              </Form.Item>
              <Form.Item
                name="comments"
                label="审批意见"
                rules={[{ required: true, message: '请输入审批意见' }]}
              >
                <TextArea rows={3} placeholder="请输入审批意见" />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button onClick={() => setModalVisible(false)}>取消</Button>
                  <Button type="primary" htmlType="submit" loading={approving}>
                    提交审批
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </div>
        )}
      </Card>
    );
  };

  // 如果有具体申请ID，直接显示详情
  if (applicationId && type) {
    return (
      <div>
        <Button 
          type="link" 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/leave-overtime/approval')}
        >
          返回审批列表
        </Button>
        {loading ? (
          <Card loading title="加载中...">
            <div style={{ minHeight: 300 }}></div>
          </Card>
        ) : (
          renderApplicationDetails()
        )}
      </div>
    );
  }

  // 否则显示审批列表
  return (
    <Card title="请假加班审批" className="approval-page">
      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        <TabPane tab="待审批" key="pending">
          {isMobile ? (
            renderMobileList('pending')
          ) : (
            <Table
              columns={pendingColumns}
              dataSource={pendingApplications}
              rowKey={(record) => record.approval_id}
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1200 }}
            />
          )}
        </TabPane>
        <TabPane tab="已审批" key="processed">
          {isMobile ? (
            renderMobileList('processed')
          ) : (
            <Table
              columns={processedColumns}
              dataSource={processedApplications}
              rowKey={(record) => record.approval_id}
              loading={loading}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1200 }}
            />
          )}
        </TabPane>
      </Tabs>

      {/* 详情弹窗 */}
      <Modal
        title="申请详情"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        {renderApplicationDetails()}
      </Modal>
    </Card>
  );
};

export default ApprovalPage;